import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, ExternalLink } from "lucide-react";
const MobileSheet = lazy(() => import("./MobileSheet"));
import { Logo } from "./Logo";
import { useApply } from "@/context/ApplyContext";
import { useSite } from "@/hooks/useSite";
import { Icon } from "./Icons";
import { HotBadge, hotGlow } from "@/components/site/HotBadge";

export const partnerLinks = (links = {}) => [
  { label: "Partner Registration", href: links.partner_registration || "#", id: "partner-registration" },
  { label: "Partner Login", href: links.partner_login || "#", id: "partner-login" },
  { label: "Own Brand Loan Agency", href: links.own_brand_agency || "#", id: "own-brand-agency" },
];

const useMenu = () => ({ home: true, loans: true, compare: true, calculators: true, partners: true, apply: true, ...(useSite().data?.settings?.menu || {}) });

const NavLinks = ({ onNav, prefix }) => {
  const m = useMenu();
  const cls = "text-[15px] font-semibold text-[var(--pp-text)] hover:text-[var(--pp-primary)] transition-colors";
  return (
    <>
      {m.home && <Link to="/" onClick={() => { onNav?.(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={cls} data-testid={`${prefix}-nav-home`}>Home</Link>}
      {m.loans && <Link to="/loans" onClick={() => onNav?.()} className={cls} data-testid={`${prefix}-nav-loans`}>Loans</Link>}
      {m.compare && <Link to="/compare" onClick={() => onNav?.()} className={cls} data-testid={`${prefix}-nav-compare`}>Compare Loans</Link>}
      {m.calculators && <Link to="/calculators" onClick={() => onNav?.()} className={cls} data-testid={`${prefix}-nav-calculators`}>Calculators</Link>}
    </>
  );
};

const PartnersMenu = ({ links }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (e.type === "keydown" ? e.key === "Escape" : !ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); };
  }, [open]);
  return (
    <div ref={ref} className="relative" onMouseLeave={() => setOpen(false)}>
      <button type="button" onClick={() => setOpen((o) => !o)} onMouseEnter={() => setOpen(true)} aria-expanded={open} aria-haspopup="menu" className="flex items-center gap-1 text-[15px] font-semibold text-[var(--pp-text)] hover:text-[var(--pp-primary)]" data-testid="header-nav-partners">
        Partners <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" className="absolute left-0 top-full pt-2 w-64 z-50">
          <div className="bg-white rounded-2xl p-2 border border-[var(--pp-border)] shadow-[var(--pp-shadow-lg)] pp-rise">
            {partnerLinks(links).map((l) => (
              <a key={l.id} role="menuitem" href={l.href} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} data-testid={`header-partners-${l.id}`} className="flex justify-between items-center font-medium rounded-xl px-3 py-2.5 hover:bg-[var(--pp-bg)]">
                {l.label} <ExternalLink size={14} className="text-[var(--pp-slate)]" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileMenu = ({ links }) => {
  const m = useMenu();
  const hotProducts = (useSite().data?.products || []).filter((p) => p.hot);
  const [open, setOpen] = useState(false);
  const { openApply } = useApply();
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="lg:hidden p-2 rounded-xl border border-[var(--pp-border)]" aria-label="Open menu" aria-expanded={open} data-testid="mobile-menu-button">
        <Menu size={22} />
      </button>
      {open && <Suspense fallback={null}><MobileSheet open={open} onOpenChange={setOpen}>
        <nav className="flex flex-col gap-5 mt-8" aria-label="Mobile">
          <NavLinks onNav={() => setOpen(false)} prefix="mobile" />
          {hotProducts.map((p) => (
            <Link key={p.slug} to={`/loans/${p.slug}`} onClick={() => setOpen(false)} className={`flex items-center gap-2 font-semibold rounded-xl border px-3 py-2.5 ${hotGlow(p)}`} data-testid={`mobile-hot-${p.slug}`}>
              <Icon name={p.icon} size={18} color={p.color} /> {p.name} <HotBadge p={p} testId={`mobile-hot-badge-${p.slug}`} />
            </Link>
          ))}
          {m.partners && <p className="pp-eyebrow mt-2">Partners</p>}
          {m.partners && partnerLinks(links).map((l) => (
            <a key={l.id} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`mobile-partners-${l.id}`} className="flex items-center gap-2 font-medium text-[var(--pp-slate)]">
              {l.label} <ExternalLink size={14} />
            </a>
          ))}
          {m.apply && <button type="button" className="pp-btn pp-btn-cta mt-4" data-testid="mobile-apply-for-loan" onClick={() => { setOpen(false); openApply(); }}>Apply For Loan</button>}
        </nav>
      </MobileSheet></Suspense>}
    </>
  );
};

export const Header = ({ links }) => {
  const { openApply } = useApply();
  const m = useMenu();
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[var(--pp-border)]" data-testid="site-header">
      <div className="pp-container h-[72px] flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-9" aria-label="Main">
          <NavLinks prefix="header" />
          {m.partners && <PartnersMenu links={links} />}
        </nav>
        <div className="flex items-center gap-3">
          {m.apply && <button type="button" onClick={() => openApply()} className="pp-btn pp-btn-cta !py-2.5 !px-4 sm:!px-5 text-sm sm:text-[15px]" data-testid="header-apply-for-loan">
            Apply For Loan
          </button>}
          <MobileMenu links={links} />
        </div>
      </div>
    </header>
  );
};
